import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { PageHeader } from "@/components/dashboard/page-header";
import { StatusPill } from "@/components/dashboard/status-pill";
import { PersonAvatar } from "@/components/employees/person-avatar";
import { BreakDecisionPanel } from "@/components/breaks/decision-panel";
import { breaks as copy, nav, status as statusCopy } from "@/content";
import { can, scopeToSupervisorId } from "@/constants/permissions";
import { getBreakRequest } from "@/api/breaks";
import { cn } from "@/utils";
import { useQuery } from "@tanstack/react-query";
import { useFmt } from "@/hooks/shared/useFmt";
import { useSession } from "@/context/UserContext";
import { RecordNotFound } from "@/components/dashboard/record-not-found";
import { SkeletonPanel } from "@/components/dashboard/skeletons";
import { AccessDenied } from "@/components/dashboard/access-denied";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";

/**
 * One momentary permission request.
 *
 * Closes the notify deep-link already used by the API (`/dashboard/breaks/:id`).
 */
export const Route = createFileRoute("/_main/dashboard/breaks/$id")({
  component: () => (
    <RequirePermission action="leave.read">
      <BreakDetailPage />
    </RequirePermission>
  ),
});

function BreakDetailPage() {
  useDocumentTitle(copy.requestTitle);
  const { user } = useSession();
  const { id } = Route.useParams();
  const requestId = Number(id);
  const validId = Number.isInteger(requestId) && requestId >= 1;

  const fmt = useFmt();

  const requestQuery = useQuery({
    queryKey: ["breaks", "get", requestId],
    queryFn: () => getBreakRequest(requestId),
    enabled: validId,
  });
  const request = requestQuery.data;

  if (!validId || (requestQuery.isFetched && !request)) return <RecordNotFound />;
  if (!request) {
    return (
      <div className="mx-auto w-full max-w-shell px-6 py-8">
        <SkeletonPanel height="24rem" />
      </div>
    );
  }

  const scope = scopeToSupervisorId(user);
  if (scope !== null && request.supervisorId !== scope) {
    return <AccessDenied role={user.role} />;
  }

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrow={nav.groups.operations}
        title={request.guardName}
        lead={request.breakType.nameAr}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/dashboard/breaks"
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-n4 bg-n0 px-4 text-sm font-semibold text-n8 transition-colors duration-200 ease-brand hover:bg-n2"
            >
              <ArrowRight aria-hidden className="size-4" strokeWidth={1.6} />
              {copy.backToQueue}
            </Link>
            <BreakDecisionPanel
              id={request.id}
              guardName={request.guardName}
              status={request.status}
              canDecide={can(user.role, "leave.decide")}
              canReopen={can(user.role, "leave.reopen")}
            />
          </div>
        }
      />

      <div className="mt-8">
        <dl className="grid gap-px overflow-hidden rounded-lg bg-n4 sm:grid-cols-2">
          <Cell label={copy.columnStatus}>
            {request.status === "pending" ? (
              <StatusPill tone="attention">{statusCopy.pending}</StatusPill>
            ) : request.status === "approved" ? (
              <StatusPill tone="positive">{statusCopy.approved}</StatusPill>
            ) : (
              <StatusPill tone="negative">{statusCopy.rejected}</StatusPill>
            )}
          </Cell>

          <Cell label={copy.columnMinutes}>
            <span className="tabular">{fmt.count(request.minutes)}</span>
            {request.fromNow ? (
              <span className="ms-2 align-middle">
                <StatusPill tone="attention">{copy.fromNow}</StatusPill>
              </span>
            ) : null}
          </Cell>

          <Cell label={copy.columnWindow} className="sm:col-span-2">
            {request.startAt && request.endAt ? (
              <span className="tabular">{fmt.range(request.startAt, request.endAt)}</span>
            ) : (
              <span>
                {request.status === "pending" ? copy.startsOnApproval : copy.notStarted}
              </span>
            )}
          </Cell>

          <Cell label={copy.columnType}>
            <span className="font-semibold">{request.breakType.nameAr}</span>
            <span className="mt-0.5 block text-[11px] text-n6">
              {request.breakType.countsAsWorked ? copy.countsAsWorked : copy.doesNotCountAsWorked}
            </span>
          </Cell>

          <Cell label={copy.guard}>
            <span className="flex items-center gap-2.5">
              <PersonAvatar name={request.guardName} photoUrl={request.photoUrl} />
              <Link
                to={`/dashboard/employees/${request.guardId}`}
                className="font-semibold text-orange-700 transition-opacity duration-200 ease-brand hover:opacity-80"
              >
                {request.guardName}
              </Link>
            </span>
          </Cell>

          <Cell label={copy.supervisor}>{request.supervisorName ?? "—"}</Cell>

          <Cell label={copy.notes} className="sm:col-span-2">
            {request.notes ? (
              <span className="leading-body">{request.notes}</span>
            ) : (
              <span className="text-n6">{copy.noNotes}</span>
            )}
          </Cell>

          {request.rejectReason ? (
            <Cell label={copy.rejectReasonLabel} className="sm:col-span-2">
              <span className="leading-body text-destructive">{request.rejectReason}</span>
            </Cell>
          ) : null}
        </dl>

        <section className="mt-8">
          <h2 className="text-sm font-semibold text-n8">{copy.timelineTitle}</h2>
          <ol className="mt-4 flex flex-col gap-4 border-s border-n3 ps-5">
            <TimelineItem
              label={copy.timelineFiled}
              when={fmt.dateTime(request.createdAt)}
              who={request.guardName}
            />
            {request.decidedAt ? (
              <TimelineItem
                label={copy.timelineDecided}
                when={fmt.dateTime(request.decidedAt)}
                who={request.decidedByName}
                detail={
                  request.status === "approved" ? statusCopy.approved : statusCopy.rejected
                }
              />
            ) : null}
            {request.reopenedAt ? (
              <TimelineItem
                label={copy.timelineReopened}
                when={fmt.dateTime(request.reopenedAt)}
                who={request.reopenedByName}
              />
            ) : null}
          </ol>
        </section>
      </div>
    </div>
  );
}

function Cell({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("bg-n0 p-5", className)}>
      <dt className="text-[11px] font-semibold tracking-[0.04em] text-n6">{label}</dt>
      <dd className="mt-2 text-sm text-n8">{children}</dd>
    </div>
  );
}

function TimelineItem({
  label,
  when,
  who,
  detail,
}: {
  label: string;
  when: string;
  who?: string | null;
  detail?: string;
}) {
  return (
    <li className="relative">
      <span aria-hidden className="absolute -start-[27px] top-1.5 size-1.5 rotate-45 bg-orange" />
      <p className="text-xs font-semibold text-n8">
        {label}
        {detail ? <span className="ms-2 font-normal text-n6">{detail}</span> : null}
      </p>
      <p className="tabular mt-0.5 text-[11px] text-n6">
        {when}
        {who ? ` · ${copy.timelineBy} ${who}` : ""}
      </p>
    </li>
  );
}
